import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { driverApi } from "../../services/driverApi";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt$ = (n) =>
  Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

const fmtTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
};

const fmtShortDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const fmtMiles = (n) =>
  typeof n === "number" && n > 0 ? `${n.toFixed(1)} mi` : null;

const STATUS_BADGE = {
  Assigned:  "bg-blue-100 text-blue-700",
  EnRoute:   "bg-yellow-100 text-yellow-800",
  PickedUp:  "bg-orange-100 text-orange-700",
  Completed: "bg-green-100 text-green-700",
  Cancelled: "bg-red-100 text-red-600",
  NoShow:    "bg-gray-100 text-gray-500",
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent = "blue" }) {
  const colors = {
    blue:  "text-blue-600",
    green: "text-green-600",
    purple:"text-purple-600",
    amber: "text-amber-600",
    gray:  "text-gray-600",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-xl font-bold ${colors[accent] || colors.blue}`}>{value}</p>
      {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function SectionHeader({ title, linkTo, linkLabel }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <p className="text-sm font-bold text-gray-800">{title}</p>
      {linkTo && (
        <Link to={linkTo} className="text-xs text-blue-600 hover:underline font-medium">{linkLabel}</Link>
      )}
    </div>
  );
}

function EnrollmentCard({ enrollment }) {
  const STEP_META = {
    profile: { label: "Complete Profile",          to: "/enrollme/driver/profile" },
    sign:    { label: "Sign Documents",            to: "/enrollme/driver/sign" },
    wc43:    { label: "WC43 Form",                 to: "/enrollme/driver/wc43" },
    uploads: { label: "Upload Compliance Docs",    to: "/enrollme/driver/uploads" },
  };
  const allDone = enrollment.percent === 100;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-gray-800">Enrollment</p>
        <span className={`text-xs font-bold ${allDone ? "text-green-600" : "text-blue-600"}`}>
          {enrollment.percent}%
        </span>
      </div>

      <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
        <div
          className={`h-full rounded-full transition-all duration-500 ${allDone ? "bg-green-500" : "bg-blue-600"}`}
          style={{ width: `${enrollment.percent}%` }}
        />
      </div>

      {allDone ? (
        <p className="text-xs text-green-700 font-medium">
          Enrollment complete — the office will review your submission.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {enrollment.steps.map((step) => {
            const meta = STEP_META[step.id];
            if (!meta) return null;
            return (
              <li key={step.id}>
                <Link
                  to={meta.to}
                  className={`flex items-center gap-2.5 text-xs rounded-lg px-2 py-1.5 transition ${
                    step.done
                      ? "text-green-700"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span className={`w-4 h-4 flex-shrink-0 flex items-center justify-center rounded-full ${
                    step.done ? "bg-green-500" : "bg-gray-200"
                  }`}>
                    {step.done ? (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                    )}
                  </span>
                  <span className={step.done ? "line-through text-green-600" : ""}>{meta.label}</span>
                  {!step.done && (
                    <svg className="w-3 h-3 ml-auto text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DriverDashboard({ driver: authDriver }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    driverApi.getDashboard()
      .then(setData)
      .catch((e) => setError(e.message || "Failed to load dashboard."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-gray-100 rounded-xl h-24 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
        {error}
      </div>
    );
  }

  const linked = data?.linked;

  return (
    <div className="space-y-5">

      {/* ── Welcome banner ── */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">
          Welcome back, {authDriver?.firstName}!
        </h1>
        {linked && data.driver?.cabNumber ? (
          <p className="text-sm text-gray-500 mt-0.5">
            Cab #{data.driver.cabNumber}
            {data.driver.vehicle ? ` · ${data.driver.vehicle}` : ""}
          </p>
        ) : (
          <p className="text-sm text-gray-500 mt-0.5">
            {linked ? "No cab currently assigned" : "Driver Portal"}
          </p>
        )}
      </div>

      {/* ── Not-linked notice ── */}
      {!linked && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-blue-900 mb-1">Dispatch account not yet linked</p>
          <p className="text-xs text-blue-700">
            Your portal account isn't connected to the dispatch system yet. Earnings and trip history
            will appear automatically once the office activates your account.
          </p>
        </div>
      )}

      {/* ── Today's snapshot ── */}
      {linked && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Today</p>
          <div className="grid grid-cols-3 gap-2">
            <StatCard
              label="Trips"
              value={data.today.trips}
              accent="blue"
            />
            <StatCard
              label="Earned"
              value={fmt$(data.today.revenue)}
              accent="green"
            />
            <StatCard
              label="Miles"
              value={data.today.miles > 0 ? `${data.today.miles.toFixed(1)}` : "—"}
              sub={data.today.miles > 0 ? "mi driven" : "no data yet"}
              accent="purple"
            />
          </div>
        </div>
      )}

      {/* ── Upcoming / active trips ── */}
      {linked && data.upcoming?.length > 0 && (
        <div>
          <SectionHeader title="Upcoming Trips" />
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <ul className="divide-y divide-gray-100">
              {data.upcoming.map((trip) => (
                <li key={trip.id} className="px-4 py-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">#{trip.bookingId ?? "—"}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${STATUS_BADGE[trip.status] || ""}`}>
                        {trip.status}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">{fmtTime(trip.pickupTime)}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-800 truncate">{trip.pickupAddress || "—"}</p>
                  {trip.dropoffAddress && (
                    <p className="text-xs text-gray-400 truncate">→ {trip.dropoffAddress}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1">
                    {trip.customerName && (
                      <span className="text-xs text-gray-500">{trip.customerName}</span>
                    )}
                    {trip.passengers > 1 && (
                      <span className="text-xs text-gray-400">{trip.passengers} passengers</span>
                    )}
                    {trip.estimatedFare > 0 && (
                      <span className="text-xs font-medium text-gray-700 ml-auto">{fmt$(trip.estimatedFare)}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* ── This month stats ── */}
      {linked && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            {new Date().toLocaleString("default", { month: "long" })}
          </p>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <StatCard
              label="Total Earned"
              value={fmt$(data.month.revenue)}
              accent="green"
            />
            <StatCard
              label="Trips Completed"
              value={data.month.trips}
              accent="blue"
            />
            <StatCard
              label="Avg Fare"
              value={data.month.trips ? fmt$(data.month.avgFare) : "—"}
              accent="purple"
            />
            <StatCard
              label="Active Days"
              value={data.month.activeDays}
              sub="days with trips"
              accent="amber"
            />
          </div>
          {(data.month.miles > 0) && (
            <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                <span className="text-sm text-gray-700">Miles driven this month</span>
              </div>
              <span className="text-sm font-bold text-gray-900">{data.month.miles.toFixed(1)} mi</span>
            </div>
          )}
          {(data.month.cancelled > 0 || data.month.noShow > 0) && (
            <p className="text-xs text-gray-400 mt-1.5 px-1">
              {data.month.cancelled} cancelled · {data.month.noShow} no-show this month
            </p>
          )}
        </div>
      )}

      {/* ── Recent trips ── */}
      {linked && data.recentTrips?.length > 0 && (
        <div>
          <SectionHeader title="Recent Trips" linkTo="/enrollme/driver/earnings" linkLabel="View all" />
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <ul className="divide-y divide-gray-100">
              {data.recentTrips.map((trip) => (
                <li key={trip.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400">{fmtShortDate(trip.tripDate)}</p>
                    <p className="text-sm text-gray-700 truncate">{trip.pickupAddress || "—"}</p>
                    {trip.dropoffAddress && (
                      <p className="text-xs text-gray-400 truncate">→ {trip.dropoffAddress}</p>
                    )}
                    {fmtMiles(trip.miles) && (
                      <p className="text-[10px] text-gray-400">{fmtMiles(trip.miles)}</p>
                    )}
                  </div>
                  <p className="text-sm font-bold text-green-600 flex-shrink-0">{fmt$(trip.fare)}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* ── Enrollment progress ── */}
      {data.enrollment && (
        <EnrollmentCard enrollment={data.enrollment} />
      )}

      {/* ── Insurance card ── */}
      {data.insuranceCard && (
        <div className="bg-white rounded-xl border border-blue-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">Insurance Card Available</p>
            {data.insuranceCard.expiryDate && (
              <p className="text-xs text-gray-500">
                Expires {new Date(data.insuranceCard.expiryDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>
            )}
          </div>
          <Link to="/enrollme/driver/uploads" className="text-sm text-blue-600 font-medium hover:underline flex-shrink-0">
            View
          </Link>
        </div>
      )}

      {/* ── Messages preview ── */}
      {data.messages?.length > 0 && (
        <div>
          <SectionHeader title="Messages from Office" linkTo="/enrollme/driver/messages" linkLabel="View all" />
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <ul className="divide-y divide-gray-100">
              {data.messages.map((m) => (
                <li key={m.id}>
                  <Link
                    to="/enrollme/driver/messages"
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition"
                  >
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{m.subject}</p>
                      <p className="text-xs text-gray-400">{fmtShortDate(m.createdAt)}</p>
                    </div>
                    <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

    </div>
  );
}
